import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { normalizeCipherState, selectConsultationsRemaining, useGameStore } from '../../store/gameStore';
import { getActionErrorPresentation } from '../../utils/actionError';
import {
  appendRevealDiff,
  applyActionResult,
  buildSessionRefreshCompletionUpdate,
  nestActionEntityState,
} from '../../utils/actionResult';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Search, MessageSquare, FileSearch, FileText, MapPin, HelpCircle, AlertTriangle, Menu, RotateCcw, FolderOpen, Home, Archive, Grid3X3, Users, Clock, Network, Plus, Settings, Lock, KeyRound, Check } from 'lucide-react';
import { DropdownMenuSeparator } from '../ui/dropdown-menu';
import { toast } from '../../hooks/use-toast';
import { gameAPI } from '../../services/gameAPI';
import { buildActionTargets, getActionEmptyHint } from '../../utils/actionTargets';
import { isFeaturedSessionId } from '../../utils/featuredSession';
import { restartFeaturedCase } from '../../utils/restartFeaturedCase';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { CaseFile } from './CaseFile';
import { CaseSummaryModal } from './CaseSummaryModal';
import { EvidenceBoard } from './evidence/EvidenceBoard';
import { SuspectWall } from './SuspectWall';
import { Timeline } from './Timeline';
import { RelationshipMap } from './RelationshipMap';
import { NewsModal } from './NewsModal';
import { InterrogationModal } from './InterrogationModal';
import { ExamineModal } from './ExamineModal';
import { CipherModal } from './CipherModal';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { KeyboardShortcutsModal } from '../ui/KeyboardShortcutsModal';
import { DiscoveryNotification } from '../ui/DiscoveryNotification';
import { SettingsMenu } from '../ui/SettingsMenu';

// How long a failed news request is re-checked; mirrors the server's NEWS_INFLIGHT_STALE.
const NEWS_RESYNC_WINDOW_MS = 300000;

const CONSULTS_EXHAUSTED_HINT = 'No consultations remaining.';

export const ActionBar = ({ sessionId, onAccuse }) => {
  const navigate = useNavigate();
  const gameState = useGameStore();
  const { suspects, locations, documents, updateGameState, selectedTheme, messageHistory, addMessage, addNewsArticle, resetGame, setSelectedCharacter, preSelectedAction, preSelectedTarget, setPreSelectedAction, cipherState, cipherPuzzle, setShowCipherModal, guideCharacter, addCipherHint } = gameState;
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedTarget, setSelectedTarget] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showNewCaseConfirm, setShowNewCaseConfirm] = useState(false);
  const [showCaseFile, setShowCaseFile] = useState(false);
  const [showCaseSummary, setShowCaseSummary] = useState(false);
  const [showEvidenceBoard, setShowEvidenceBoard] = useState(false);
  const [showSuspectWall, setShowSuspectWall] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showRelationshipMap, setShowRelationshipMap] = useState(false);
  const [showNews, setShowNews] = useState(false);
  const [currentNews, setCurrentNews] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const newsRequestToken = React.useRef(0);
  const undisplayedNews = React.useRef(null);
  // Set when a news request fails: the server may still save that article.
  // { sessionId, until, seenCount } — held until the article arrives or `until` passes.
  const newsResync = React.useRef(null);
  const [showInterrogation, setShowInterrogation] = useState(false);
  const [interrogationSuspect, setInterrogationSuspect] = useState(null);
  const [interrogationRoundsCompleted, setInterrogationRoundsCompleted] = useState(0);
  const [previousNarrative, setPreviousNarrative] = useState('');
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showExamineModal, setShowExamineModal] = useState(false);
  const [examineLocation, setExamineLocation] = useState(null);
  // Structured retry state for failed retryable actions (mirrors InterrogationModal pattern).
  // lastFailedAction: { actionType, target } snapshot — captured at failure time so
  // the user can press "Try Again" even if they tweak the dropdowns afterward.
  const [lastFailedAction, setLastFailedAction] = useState(null);
  const [actionError, setActionError] = useState(null);

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || 'noir'];

  // Auto-populate action and target from pre-selection
  useEffect(() => {
    if (preSelectedAction) {
      setSelectedAction(preSelectedAction);
      setSelectedTarget(preSelectedTarget || '');
    }
  }, [preSelectedAction, preSelectedTarget]);

  // Close all modals helper
  const closeAllModals = () => {
    setShowCaseFile(false);
    setShowEvidenceBoard(false);
    setShowSuspectWall(false);
    setShowTimeline(false);
    setShowRelationshipMap(false);
    setShowKeyboardShortcuts(false);
    setShowResetConfirm(false);
    setShowNewCaseConfirm(false);
    setShowSettings(false);
    setShowExamineModal(false);
    setShowCipherModal(false);
  };

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onExamine: () => setSelectedAction('examine'),
    onInterview: () => setSelectedAction('interview'),
    onTravel: () => setSelectedAction('travel'),
    onRead: () => setSelectedAction('read'),
    onConsult: () => setSelectedAction('consult_guide'),
    onCaseFile: () => setShowCaseFile(true),
    onEvidenceBoard: () => setShowEvidenceBoard(true),
    onSuspectWall: () => setShowSuspectWall(true),
    onTimeline: () => setShowTimeline(true),
    onHelp: () => setShowKeyboardShortcuts(true),
    onEscape: closeAllModals,
  });

  // Incrementing counter signals an external consult request from LeftPanel.
  // Bumping the counter (rather than relying on selectedAction transitions)
  // guarantees the auto-execute effect re-runs even when consult_guide is
  // already the selected action.
  const [consultTrigger, setConsultTrigger] = useState(0);

  // Ref to hold latest handleAction - avoids stale closure in auto-execute effect
  const handleActionRef = React.useRef(null);

  const refetchSession = React.useCallback(async ({ revealDiff = null, silent = false } = {}) => {
    if (!sessionId) return;

    updateGameState({ needsSessionRefresh: true, actionProcessing: true });
    setProcessing(true);

    try {
      const savedData = await gameAPI.loadGameSession(sessionId);
      useGameStore.getState().loadSavedGame({
        theme: savedData.theme || selectedTheme || 'noir',
        difficulty: savedData.difficulty || gameState.selectedDifficulty || 'medium',
        caseId: savedData.caseId,
        sessionId: savedData.sessionId,
        gameState: savedData.gameState,
        messageHistory: savedData.messageHistory,
        guideCharacter: savedData.guideCharacter,
        caseData: savedData.caseData,
        lastImage: savedData.lastImage,
        lastImageVantage2: savedData.lastImageVantage2,
      });

      if (revealDiff) {
        const refreshedState = useGameStore.getState();
        useGameStore.getState().updateGameState(buildSessionRefreshCompletionUpdate({
          didRefresh: true,
          currentNewlyDiscovered: refreshedState.newlyDiscovered,
          revealDiff,
        }));
      } else {
        useGameStore.getState().updateGameState(buildSessionRefreshCompletionUpdate({
          didRefresh: true,
        }));
      }

      if (!silent) {
        toast({ title: 'Case file refreshed', description: 'Your session is back in sync.' });
      }
      return true;
    } catch (error) {
      console.error('Error refreshing session:', error);
      useGameStore.getState().updateGameState(buildSessionRefreshCompletionUpdate({
        didRefresh: false,
      }));
      toast({
        title: 'Refresh failed',
        description: 'Could not reload the latest case file. Please try again.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setProcessing(false);
    }
  }, [gameState.selectedDifficulty, selectedTheme, sessionId, updateGameState]);

  // Listen for consult trigger from LeftPanel
  useEffect(() => {
    const handleTriggerConsult = () => {
      setSelectedAction('consult_guide');
      setConsultTrigger(c => c + 1);
    };
    window.addEventListener('triggerConsult', handleTriggerConsult);
    return () => window.removeEventListener('triggerConsult', handleTriggerConsult);
  }, []);

  // Listen for an examine request from an examine point in the 3D room
  // (Game3DPage). It takes the Go button's own path with EXAMINE and the
  // requested location, so the room opens the identical ExamineModal.
  useEffect(() => {
    const handleTriggerExamine = (event) => {
      const locationId = event.detail?.locationId;
      const store = useGameStore.getState();
      // Only a known location: handleAction would otherwise fall through to a
      // plain EXAMINE request instead of opening the modal.
      if (!locationId || !(store.locations || []).some((location) => location.id === locationId)) return;
      // The Go button is disabled while an action is in flight; drop the
      // request the same way rather than stacking the modal on top of it.
      if (store.actionProcessing) return;
      handleActionRef.current?.('examine', locationId);
    };
    window.addEventListener('triggerExamine', handleTriggerExamine);
    return () => window.removeEventListener('triggerExamine', handleTriggerExamine);
  }, []);

  // Mirror local `processing` to the store so LeftPanel can disable the
  // Consult button while an action is in flight (prevents phantom consults
  // from clicks that land mid-action).
  useEffect(() => {
    updateGameState({ actionProcessing: processing });
  }, [processing, updateGameState]);

  // Defensive: if ActionBar unmounts mid-action (e.g. user navigates away
  // while processing), clear the store flag so the next mount doesn't
  // inherit a stale `actionProcessing: true`. Separate from the mirror
  // effect above to avoid redundant writes on every `processing` toggle.
  useEffect(() => {
    return () => {
      updateGameState({ actionProcessing: false });
    };
  }, [updateGameState]);

  // Auto-execute when an external consult trigger fires.
  // Note: deps intentionally exclude `processing` — we don't want a deferred
  // fire when processing flips false later (that's the phantom-consult bug).
  // LeftPanel disables the button while processing, so this is also a
  // defense-in-depth guard if something slips past.
  useEffect(() => {
    if (consultTrigger === 0) return; // initial mount
    if (processing) return; // drop the request rather than queue it
    if (handleActionRef.current) {
      handleActionRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultTrigger]);

  // Everything the shared target rules need. Kept as one memo so the action
  // list, the target list and the preselect guard all read the same snapshot.
  const targetState = React.useMemo(() => ({
    suspects,
    locations,
    documents,
    discovered: gameState.discovered,
    interrogationDialogues: gameState.interrogationDialogues,
    examinations: gameState.examinations,
    examinedFocuses: gameState.examinedFocuses,
    selectedDifficulty: gameState.selectedDifficulty,
  }), [
    suspects,
    locations,
    documents,
    gameState.discovered,
    gameState.interrogationDialogues,
    gameState.examinations,
    gameState.examinedFocuses,
    gameState.selectedDifficulty,
  ]);

  // Consult has no targets, so it is gated on the budget instead; the server
  // refuses a consult past the limit before anything else runs.
  const consultsExhausted = selectConsultationsRemaining(gameState) <= 0;

  const actions = [
    { id: 'examine', label: 'Examine Location', icon: Search },
    { id: 'interview', label: 'Interview Suspect', icon: MessageSquare },
    { id: 'read', label: 'Read Document', icon: FileSearch },
    { id: 'travel', label: 'Travel To', icon: MapPin },
    { id: 'consult_guide', label: `Consult ${guideCharacter?.name?.split(' ').pop() || 'Guide'}`, icon: HelpCircle },
  ].map((action) => {
    // An action with nothing left to act on stays visible but disabled with a
    // short reason — hiding it would make the bar jump around mid-case. The
    // reason distinguishes "none discovered yet" (turn 0) from "all used up".
    const hint = action.id === 'consult_guide'
      ? (consultsExhausted ? CONSULTS_EXHAUSTED_HINT : null)
      : getActionEmptyHint(action.id, targetState);
    return { ...action, disabled: Boolean(hint), hint };
  });

  const handleAction = async (overrideAction = null, overrideTarget = null) => {
    // Retry path supplies overrides so a fresh dropdown change doesn't repaint
    // the failed request. Otherwise read from current state.
    const actionToRun = overrideAction ?? selectedAction;
    const targetToRun = overrideTarget ?? selectedTarget;

    if (!sessionId) {
      toast({ title: 'Error', description: 'Game session not initialized' });
      return;
    }

    if (!actionToRun) {
      toast({ title: 'Select an action', description: 'Please choose what you want to do' });
      return;
    }

    if (actionToRun !== 'consult_guide' && !targetToRun) {
      toast({ title: 'Select a target', description: 'Please choose a target for your action' });
      return;
    }

    // Intercept interview actions to use interrogation system
    if (actionToRun === 'interview' && targetToRun) {
      // A won/lost case stays on the board, but its suspects can no longer be
      // questioned; the backend 409s the request (PG-21).
      if (isGameOver) return;
      const suspect = (suspects || []).find(s => s.id === targetToRun);
      if (suspect) {
        // Show suspect portrait in left panel immediately
        setSelectedCharacter({ ...suspect, type: 'suspect' });
        // Preserve current narrative in case user cancels (do not clear the
        // store narrativeText — an empty value trips the session-authority guard)
        setPreviousNarrative(gameState.narrativeText);
        // Open modal immediately to show loading state
        setInterrogationSuspect(suspect);
        setInterrogationRoundsCompleted(0); // Will be updated by modal
        setShowInterrogation(true);
        setSelectedTarget('');
        // Clear pre-selection after opening interrogation
        if (preSelectedAction) {
          setPreSelectedAction(null, null);
        }
        return;
      }
    }

    // Intercept examine actions to show focus selection modal
    if (actionToRun === 'examine') {
      // Use current location if no target selected
      const locationId = targetToRun || gameState.currentLocation;
      const location = (locations || []).find(l => l.id === locationId);
      if (location) {
        setExamineLocation(location);
        setShowExamineModal(true);
        // Clear selectedTarget to prevent stale state on next action
        setSelectedTarget('');
        // Clear pre-selection after opening modal
        if (preSelectedAction) {
          setPreSelectedAction(null, null);
        }
        return;
      }
    }

    try {
      setProcessing(true);

      // Check if this exact action+target was already performed SUCCESSFULLY
      // Use case-insensitive comparison for action types
      // Travel updates the backend location; each consult needs a fresh answer.
      const previousMessage = actionToRun !== 'travel' && actionToRun !== 'consult_guide' ? messageHistory.find(
        msg => msg.action?.toLowerCase() === actionToRun?.toLowerCase() && msg.target === targetToRun && msg.narrative
      ) : null;

      if (previousMessage) {
        // Reuse the previous SUCCESSFUL response instead of calling API again
        // (Travel and consult always reach the backend.)
        const stateUpdate = {
          narrativeText: previousMessage.narrative,
        };

        updateGameState(stateUpdate);

        // Clear only the target, keep the action selected for consistency
        setSelectedTarget('');

        toast({
          title: 'Previous investigation',
          description: 'Reviewing what you already discovered...'
        });

        setProcessing(false);
        return;
      }

      // Call backend API for new action OR retry failed action
      const response = await gameAPI.processAction(
        sessionId,
        actionToRun,
        actionToRun === 'consult_guide' ? null : targetToRun
      );

      const actionResult = applyActionResult(gameState, response);
      const revealDiff = {
        ...actionResult.revealDiff,
        locations: actionResult.revealDiff.locations.filter(
          id => !(actionToRun === 'travel' && id === targetToRun)
        ),
      };
      const entityState = nestActionEntityState(actionResult.nextState);
      const updatedCipherState = response.updatedState?.cipherState ?? response.updatedState?.cipher_state;
      const queuedCipherHints = Array.isArray(response.revealedInfo?.cipher_hints_queued)
        ? response.revealedInfo.cipher_hints_queued
        : [];
      const queuedCipherHintIds = new Set(queuedCipherHints);
      const cipherHintsFound = (response.revealedInfo?.cipher_hints_found || [])
        .filter(id => !queuedCipherHintIds.has(id));

      // Store message in history with revealed info for Timeline tracking
      if (response.narrative) {
        const revealedInfo = {
          new_suspects: revealDiff.suspects,
          new_clues: revealDiff.clues,
          new_locations: revealDiff.locations,
          new_documents: revealDiff.documents
        };
        addMessage(actionToRun, targetToRun, response.narrative, revealedInfo);
      }

      updateGameState({
        narrativeText: response.narrative,
        discovered: entityState.discovered,
        suspects: entityState.suspects,
        clues: entityState.clues,
        locations: entityState.locations,
        documents: entityState.documents,
        newlyDiscovered: actionResult.needsSessionRefresh
          ? gameState.newlyDiscovered
          : appendRevealDiff(gameState.newlyDiscovered, revealDiff),
        needsSessionRefresh: actionResult.needsSessionRefresh,
        hintedClues: response.updatedState?.hintedClues || gameState.hintedClues,
        newlyHinted: [
          ...(gameState.newlyHinted || []),
          ...(response.revealedInfo?.new_hinted_clues || []).filter(
            id => !(gameState.hintedClues || []).includes(id)
          )
        ],
        // Any clue-granting action can move a location's examine counter — a
        // CONSULT grant lands on a roster clue at an already-examined location
        // just as a cipher reward does. The backend re-derives
        // `clues_known_at_location` on every payload (game.py's
        // `annotate_examinations`), so take the records whenever the response
        // carries them; otherwise the badge reads stale until a page reload.
        examinations: response.updatedState?.examinations || gameState.examinations,
        currentLocation: response.updatedState?.currentLocation || gameState.currentLocation,
        turnCount: response.updatedState?.turnCount ?? gameState.turnCount,
        consultationsUsed: response.updatedState?.consultationsUsed,
        consultations: response.updatedState?.consultations || gameState.consultations,
        consultationLimit: response.updatedState?.consultationLimit || gameState.consultationLimit,
        currentImage: response.newImage || gameState.currentImage,
        currentImageVantage2: response.newImageVantage2 || gameState.currentImageVantage2,
        ...(updatedCipherState !== undefined && {
          cipherState: normalizeCipherState(updatedCipherState, gameState.cipherState),
        }),
        ...(response.updatedState?.cipherPuzzle !== undefined && { cipherPuzzle: response.updatedState.cipherPuzzle }),
      });

      if (queuedCipherHints.length > 0) {
        toast({ title: 'You found a cipher-related clue. It will matter once you find the coded message.' });
      }

      cipherHintsFound.forEach(hintId => {
        addCipherHint(hintId);
      });

      // Note: Interview actions are now handled by InterrogationModal

      // Clear only the target, keep the action selected for easier repeated use
      setSelectedTarget('');
      // Don't clear selectedAction to maintain persistence

      // Clear pre-selection after successful action execution
      if (preSelectedAction) {
        setPreSelectedAction(null, null);
      }

      // Clear any prior retry banner now that an action succeeded.
      setActionError(null);
      setLastFailedAction(null);

      if (actionResult.needsSessionRefresh) {
        await refetchSession({ revealDiff, silent: true });
      } else {
        toast(response.consultExhausted
          ? { title: 'Your contact has nothing new for you. No consult used.' }
          : { title: 'Action completed', description: 'Your investigation continues...' });
      }

      // Keep the action locked until the news request completes.
      await checkAndGenerateNews(sessionId);

    } catch (error) {
      console.error('Error processing action:', error);
      const { message, retryable } = getActionErrorPresentation(error);

      if (retryable) {
        // Show inline banner + Try Again button. Keep selectedAction/selectedTarget
        // intact so the UI reflects what failed.
        setActionError(message);
        setLastFailedAction({ actionType: actionToRun, target: targetToRun });
      } else {
        setActionError(null);
        setLastFailedAction(null);
        toast({
          title: 'Error',
          description: message,
          variant: 'destructive'
        });
      }
      // Don't clear action/target on error so user can retry
    } finally {
      setProcessing(false);
    }
  };

  // Retry the last failed retryable action using the snapshot captured at failure.
  const handleRetryAction = () => {
    if (!lastFailedAction) return;
    const { actionType, target } = lastFailedAction;
    setActionError(null);
    // Note: we don't clear lastFailedAction yet — if the retry also fails,
    // the error handler will overwrite it. On success, the success path clears both.
    handleAction(actionType, target);
  };

  // Keep handleActionRef updated with the latest handleAction implementation
  handleActionRef.current = handleAction;

  // Discovered targets that still have something left to do:
  // INTERVIEW drops fully interrogated suspects, EXAMINE drops locations with
  // no rounds/focuses left; TRAVEL and READ are never filtered. The rules read
  // only the player's own completed rounds, so the list is identical in shape
  // for every suspect (no killer tell).
  const currentTargets = React.useMemo(
    () => buildActionTargets(selectedAction, targetState),
    [selectedAction, targetState]
  );

  const getTargets = () => currentTargets;

  const needsTarget = Boolean(selectedAction) && selectedAction !== 'consult_guide';
  const noTargetsLeft = needsTarget && currentTargets.length === 0;
  // Covers every way consult gets selected: dropdown, keyboard, RightPanel.
  const consultBlocked = selectedAction === 'consult_guide' && consultsExhausted;
  const emptyTargetHint = consultBlocked
    ? CONSULTS_EXHAUSTED_HINT
    : getActionEmptyHint(selectedAction, targetState) || 'Nothing left to choose here.';

  // A preselected target that the player has since used up must not linger in
  // the trigger (it would submit an action the backend now refuses).
  useEffect(() => {
    if (!needsTarget || !selectedTarget) return;
    if (currentTargets.some(target => target.value === selectedTarget)) return;
    setSelectedTarget('');
    // Drop the whole preselection, not just its target: leaving the action
    // behind lets the mount effect (or any later read of it) restore the
    // action the player can no longer perform.
    if (preSelectedTarget && preSelectedTarget === selectedTarget) {
      setPreSelectedAction(null, null);
    }
  }, [needsTarget, selectedTarget, currentTargets, preSelectedAction, preSelectedTarget, setPreSelectedAction]);

  // A featured case is restartable server-side; a generated one is not.
  const isFeatured = isFeaturedSessionId(sessionId);
  // `PLAYABLE_GAME_STATUSES` keeps won/lost on the board, so the bar is still
  // mounted after an accusation resolves.
  const isGameOver = gameState.gameStatus === 'won' || gameState.gameStatus === 'lost';

  const handleReset = async () => {
    // A featured case is restartable server-side: the same authored case
    // replays from turn 0 and the player stays on the game screen. Only a
    // generated case (which has no restart endpoint) is abandoned locally.
    if (isFeaturedSessionId(sessionId)) {
      setProcessing(true);
      const restarted = await restartFeaturedCase(sessionId);
      setProcessing(false);
      if (restarted) {
        setSelectedAction('');
        setSelectedTarget('');
        setActionError(null);
        setLastFailedAction(null);
        setShowResetConfirm(false);
      }
      return;
    }
    resetGame();
    navigate('/');
    setShowResetConfirm(false);
  };

  const handleNewCase = () => {
    resetGame();
    navigate('/');
    setShowNewCaseConfirm(false);
  };

  // Only the newest news request owns the modal; every article still reaches the store.
  const runNewsGeneration = async (newsSessionId) => {
    const token = ++newsRequestToken.current;
    undisplayedNews.current = null;
    setCurrentNews(null);
    setNewsLoading(true);
    setShowNews(true);
    try {
      const newsResponse = await gameAPI.generateNews(newsSessionId);
      if (newsResponse.hasNews && newsResponse.article) {
        addNewsArticle(newsResponse.article);
        if (newsResync.current?.sessionId === newsSessionId) {
          // A normal arrival is not a late one; do not re-show it at the next resync.
          newsResync.current.seenCount = (useGameStore.getState().newsArticles || []).length;
        }
        if (token === newsRequestToken.current) {
          setCurrentNews(newsResponse.article);
          setNewsLoading(false);
          undisplayedNews.current = null;
        } else {
          undisplayedNews.current = newsResponse.article;
        }
      } else if (token === newsRequestToken.current) {
        if (undisplayedNews.current) {
          setCurrentNews(undisplayedNews.current);
          undisplayedNews.current = null;
        } else {
          setShowNews(false);
        }
        setNewsLoading(false);
      }
    } catch (newsError) {
      console.error('Error checking news:', newsError);
      if (!isFeaturedSessionId(newsSessionId)) {
        newsResync.current = {
          sessionId: newsSessionId,
          until: Date.now() + NEWS_RESYNC_WINDOW_MS,
          seenCount: (useGameStore.getState().newsArticles || []).length,
        };
      }
      if (token === newsRequestToken.current) {
        setNewsLoading(false);
        if (undisplayedNews.current) {
          setCurrentNews(undisplayedNews.current);
          undisplayedNews.current = null;
        } else {
          setShowNews(false);
          toast({
            title: 'No edition today',
            description: 'The presses jammed on this story. It may run after your next move.',
          });
          return true;
        }
      }
    }
    return false;
  };

  // A failed request can still be saved server-side; pick it up at a later check.
  const resyncNewsAfterFailure = async (newsSessionId) => {
    if (newsResync.current?.sessionId !== newsSessionId) return;
    // Past the window: one final check, then the flag clears whatever the result.
    const flag = newsResync.current;
    const expired = Date.now() > flag.until;
    try {
      const { articles } = await gameAPI.getNewsHistory(newsSessionId);
      const store = useGameStore.getState();
      const pending = newsResync.current;
      if (store.sessionId !== newsSessionId || !Array.isArray(articles)) return;
      // The store gained articles during the await: this response is stale; keep the flag.
      if (articles.length < (store.newsArticles || []).length) return;
      if (pending?.sessionId !== newsSessionId || articles.length <= pending.seenCount) return;
      newsResync.current = null;
      // The server list is authoritative (as in loadSavedGame); articles have no id to merge on.
      store.updateGameState({ newsArticles: articles });
      setCurrentNews(articles[articles.length - 1]);
      setNewsLoading(false);
      setShowNews(true);
    } catch (historyError) {
      console.error('Error resyncing news history:', historyError);
    } finally {
      if (expired && newsResync.current === flag) newsResync.current = null;
    }
  };

  const checkAndGenerateNews = async (newsSessionId) => {
    await resyncNewsAfterFailure(newsSessionId);
    try {
      const triggerCheck = await gameAPI.checkNewsTrigger(newsSessionId);
      if (triggerCheck.hasTrigger) return await runNewsGeneration(newsSessionId);
    } catch (newsError) {
      console.error('Error checking news trigger:', newsError);
    }
    return false;
  };

  // Handle examine completion from ExamineModal
  // The modal now handles API calls and state updates internally
  // Note: examineLocation is cleared in the modal's onClose handler, not here
  const handleExamineComplete = async (result) => {
    // Clear action selection (location is cleared by modal's onClose)
    setSelectedTarget('');

    // Show completion toast if there were results
    // Normalize totalCluesFound to array for safety
    const cluesFound = Array.isArray(result?.totalCluesFound) ? result.totalCluesFound : [];
    if (cluesFound.length > 0) {
      toast({
        title: 'Examination complete',
        description: `Found ${cluesFound.length} piece(s) of evidence.`,
      });
    }

    await checkAndGenerateNews(sessionId);
  };

  // Handle interrogation completion
  const handleInterrogationComplete = async (result) => {
    if (!result) return;

    const interrogationActionResult = result.updated_state
      ? applyActionResult(useGameStore.getState(), { updated_state: result.updated_state })
      : null;
    const interrogationRevealDiff = interrogationActionResult?.revealDiff || null;

    // Add to message history for dialogues with revealed info for Timeline tracking
    if (interrogationSuspect && result.narrative) {
      // Combine revealed info from all rounds (only truly discovered items, not hinted)
      const combinedRevealed = {
        new_suspects: interrogationRevealDiff?.suspects || [],
        new_clues: interrogationRevealDiff?.clues || [],
        new_locations: interrogationRevealDiff?.locations || [],
        new_documents: interrogationRevealDiff?.documents || []
      };

      if (!interrogationRevealDiff) {
        (result.allResults || [result]).forEach(roundResult => {
          if (roundResult.revealed) {
            combinedRevealed.new_suspects.push(...(roundResult.revealed.new_suspects || []));
            combinedRevealed.new_clues.push(...(roundResult.revealed.new_clues || []));
            combinedRevealed.new_locations.push(...(roundResult.revealed.new_locations || []));
            combinedRevealed.new_documents.push(...(roundResult.revealed.new_documents || []));
          }
        });
      }

      addMessage('interrogation', interrogationSuspect.id, result.narrative, combinedRevealed);
    }

    // Update interrogationDialogues with structured data and narrative
    // Use allResults if available (multiple rounds) or create single entry
    if (interrogationSuspect) {
      const suspectId = interrogationSuspect.id;
      // Use getState() to get fresh state to avoid stale closure issues
      const freshState = useGameStore.getState();
      const currentDialogues = freshState.interrogationDialogues || {};
      const suspectDialogues = currentDialogues[suspectId] || [];

      // If we have allResults, create separate entries for each round
      // Otherwise fall back to single entry
      const resultsToProcess = result.allResults || (result.narrative ? [result] : []);

      const newDialogues = resultsToProcess.map((roundResult, idx) => ({
        narrative: roundResult.narrative,
        approach: roundResult.approach || 'unknown',
        roundNumber: roundResult.round_completed || roundResult.round_number || (suspectDialogues.length + idx + 1),
        effectiveness: roundResult.effectiveness ?? 0.5,
        timestamp: new Date().toISOString()
      }));

      if (newDialogues.length > 0) {
        updateGameState({
          interrogationDialogues: {
            ...currentDialogues,
            [suspectId]: [...suspectDialogues, ...newDialogues]
          },
          narrativeText: result.narrative // Combined narrative for display
        });
      }
    } else if (result.narrative) {
      // Fallback: Update narrative even if suspect data is missing
      updateGameState({
        narrativeText: result.narrative
      });
    }

    // Update discovered items from interrogation
    if (result.updated_state) {
      // Read fresh state to avoid stale closure — same pattern as interrogationDialogues above
      const latestState = useGameStore.getState();
      const freshActionResult = applyActionResult(latestState, { updated_state: result.updated_state });
      const freshRevealDiff = freshActionResult.revealDiff;
      const entityState = nestActionEntityState(freshActionResult.nextState);

      updateGameState({
        discovered: entityState.discovered,
        suspects: entityState.suspects,
        clues: entityState.clues,
        locations: entityState.locations,
        documents: entityState.documents,
        newlyDiscovered: freshActionResult.needsSessionRefresh
          ? latestState.newlyDiscovered
          : appendRevealDiff(latestState.newlyDiscovered, freshRevealDiff),
        needsSessionRefresh: freshActionResult.needsSessionRefresh,
        hintedClues: result.updated_state.hintedClues || latestState.hintedClues,
        newlyHinted: [
          ...(latestState.newlyHinted || []),
          ...((result.allResults || [result]).flatMap(r => r.revealed?.new_hinted_clues || [])
            .filter(id => !(latestState.hintedClues || []).includes(id)))
        ],
        // Interrogation is the most common way a roster clue arrives after the
        // player has already examined its location (90s_nyc suspect_3.round2
        // grants clue_6 at victim_workplace), so the refreshed counter travels
        // with the grant. `updated_state` is snake here — the conduct route
        // ships `updated_state` while `/game/action` ships `updatedState`
        // (compat.py:10-12) — but `examinations` itself is the same key and
        // shape in both.
        examinations: result.updated_state.examinations || latestState.examinations,
        turnCount: result.updated_state.turnCount,
      });

      if (freshActionResult.needsSessionRefresh) {
        await refetchSession({ revealDiff: freshRevealDiff, silent: true });
      }
    }

    const completedRounds = result.allResults || [result];
    const queuedCipherHints = completedRounds.flatMap(r => r.revealed?.cipher_hints_queued || []);
    const queuedCipherHintIds = new Set(queuedCipherHints);
    const cipherHintsFound = completedRounds.flatMap(r => r.revealed?.cipher_hints_found || [])
      .filter(id => !queuedCipherHintIds.has(id));
    if (completedRounds.some(r => !r.resumed && r.revealed?.cipher_hints_queued?.length > 0)) {
      toast({ title: 'You found a cipher-related clue. It will matter once you find the coded message.' });
    }
    [...new Set(cipherHintsFound)].forEach(hintId => {
      addCipherHint(hintId);
    });

    // Clear preserved narrative since interrogation completed successfully
    setPreviousNarrative('');

    const newsFailureShown = await checkAndGenerateNews(sessionId);

    if (!newsFailureShown) {
      toast({ title: 'Interrogation complete', description: 'Your investigation continues...' });
    }
  };

  // Menu content shared across breakpoints
  const menuContent = (
    <DropdownMenuContent align="start" className="bg-slate-900 border-white/20">
      <DropdownMenuItem
        onClick={() => navigate('/')}
        className="text-white hover:bg-white/10 cursor-pointer"
      >
        <Home className="w-4 h-4 mr-2" />
        Main Menu
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => navigate('/archive')}
        className="text-white hover:bg-white/10 cursor-pointer"
      >
        <Archive className="w-4 h-4 mr-2" />
        Case Archive
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => setShowNewCaseConfirm(true)}
        className="text-white hover:bg-white/10 cursor-pointer"
      >
        <Plus className="w-4 h-4 mr-2" />
        New Case
      </DropdownMenuItem>
      <DropdownMenuSeparator className="bg-white/10" />
      <DropdownMenuItem
        onClick={() => setShowTimeline(true)}
        className="text-white hover:bg-white/10 cursor-pointer"
      >
        <Clock className="w-4 h-4 mr-2" />
        Timeline
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => setShowRelationshipMap(true)}
        className="text-white hover:bg-white/10 cursor-pointer"
      >
        <Network className="w-4 h-4 mr-2" />
        Relationships
      </DropdownMenuItem>
      <DropdownMenuSeparator className="bg-white/10" />
      <DropdownMenuItem
        onClick={() => setShowKeyboardShortcuts(true)}
        className="text-white hover:bg-white/10 cursor-pointer"
      >
        <span className="w-4 h-4 mr-2 font-mono text-xs">⌨</span>
        Keyboard Shortcuts
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => setShowSettings(true)}
        className="text-white hover:bg-white/10 cursor-pointer"
      >
        <Settings className="w-4 h-4 mr-2" />
        Settings
      </DropdownMenuItem>
      {/* Reachable for as long as the finished case stays on screen: the
          accusation result view is the only other way in, and closing it used
          to strand the summary for good. */}
      {isGameOver && (
        <DropdownMenuItem
          onClick={() => setShowCaseSummary(true)}
          className="text-white hover:bg-white/10 cursor-pointer"
          data-testid="menu-case-summary"
        >
          <FileText className="w-4 h-4 mr-2" />
          Case Summary
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator className="bg-white/10" />
      <DropdownMenuItem
        onClick={() => setShowResetConfirm(true)}
        className="text-red-400 hover:text-red-300 hover:bg-red-900/20 cursor-pointer"
        data-testid="menu-reset-case"
      >
        <RotateCcw className="w-4 h-4 mr-2" />
        {/* A featured case replays server-side; a generated one is abandoned.
            The confirm dialog already draws this distinction. */}
        {isFeatured ? 'Restart Case' : 'Reset Case'}
      </DropdownMenuItem>
    </DropdownMenuContent>
  );

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-white/10 bg-black/60 backdrop-blur-md p-2 md:p-3">
      {gameState.needsSessionRefresh && (
        <div className="mb-2 flex flex-col sm:flex-row sm:items-center gap-2 bg-amber-900/30 border border-amber-500/40 rounded-md px-3 py-2">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-300 flex-shrink-0" />
            <p className="text-sm text-amber-100 break-words">
              Case file refresh needed before newly found evidence can appear.
            </p>
          </div>
          <Button
            onClick={() => refetchSession()}
            disabled={processing}
            size="sm"
            variant="outline"
            className="border-amber-400/40 bg-amber-900/40 text-amber-100 hover:bg-amber-800/60 hover:text-white"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            Reload case
          </Button>
        </div>
      )}

      {/* Retryable error banner (e.g. backend 503 with retryable: true).
          Mirrors the InterrogationModal retry UX — inline message + Try Again. */}
      {actionError && (
        <div className="mb-2 flex flex-col sm:flex-row sm:items-center gap-2 bg-red-900/30 border border-red-500/40 rounded-md px-3 py-2">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-red-300 flex-shrink-0" />
            <p className="text-sm text-red-100 break-words">{actionError}</p>
          </div>
          {lastFailedAction && (
            <Button
              onClick={handleRetryAction}
              disabled={processing}
              size="sm"
              variant="outline"
              className="border-red-400/40 bg-red-900/40 text-red-100 hover:bg-red-800/60 hover:text-white"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
              Try Again
            </Button>
          )}
        </div>
      )}

      {/* Mobile Layout - stacked rows (hidden on md+) */}
      <div className="flex flex-col gap-2 md:hidden">
        {/* Row 1: Menu + Quick Actions + Accuse */}
        <div className="flex items-center gap-2">
          {/* Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-touch min-h-touch bg-black/40 border-white/20 text-white hover:bg-white/10"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            {menuContent}
          </DropdownMenu>

          {/* Quick Action Buttons - flex to fill space */}
          <Button
            onClick={() => setShowCaseFile(true)}
            variant="outline"
            className="min-h-touch flex-1 bg-black/40 border-white/20 text-white hover:bg-white/10 text-xs"
            style={{ borderColor: `${themeAccent}40` }}
          >
            <FolderOpen className="w-4 h-4" />
          </Button>

          <Button
            onClick={() => setShowEvidenceBoard(true)}
            variant="outline"
            className="min-h-touch flex-1 bg-black/40 border-white/20 text-white hover:bg-white/10 text-xs"
            style={{ borderColor: `${themeAccent}40` }}
          >
            <Grid3X3 className="w-4 h-4" />
          </Button>

          <Button
            onClick={() => setShowSuspectWall(true)}
            variant="outline"
            className="min-h-touch flex-1 bg-black/40 border-white/20 text-white hover:bg-white/10 text-xs"
            style={{ borderColor: `${themeAccent}40` }}
          >
            <Users className="w-4 h-4" />
          </Button>

          {/* Cipher Button - only shows when unlocked */}
          {cipherState?.unlocked && (
            <Button
              onClick={() => setShowCipherModal(true)}
              disabled={processing}
              variant="outline"
              className={`min-h-touch flex-1 text-xs ${
                cipherState?.solved
                  ? 'bg-green-900/40 border-green-500/40 text-green-400 hover:bg-green-900/60'
                  : 'bg-black/40 text-white hover:bg-white/10'
              }`}
              style={{ borderColor: cipherState?.solved ? undefined : `${themeAccent}40` }}
            >
              {cipherState?.solved ? (
                <Check className="w-4 h-4" />
              ) : (
                <KeyRound className="w-4 h-4" style={{ color: themeAccent }} />
              )}
            </Button>
          )}

          {/* Accuse Button */}
          <Button
            onClick={onAccuse}
            disabled={isGameOver}
            data-testid="mobile-accuse"
            variant="destructive"
            className="min-h-touch px-3 font-semibold bg-red-600 hover:bg-red-700"
          >
            <AlertTriangle className="w-4 h-4" />
          </Button>
        </div>

        {/* Row 2: Action Selection + Target + Execute */}
        <div className="flex items-center gap-2">
          {/* Action Selection */}
          <Select value={selectedAction} onValueChange={(value) => {
            setSelectedAction(value);
            setActionError(null);
            setLastFailedAction(null);
          }}>
            <SelectTrigger className="min-h-touch flex-1 bg-black/40 border-white/20 text-white text-xs">
              <SelectValue placeholder="Choose action..." />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/20">
              {actions.map((action) => {
                const Icon = action.icon;
                return (
                  <SelectItem
                    key={action.id}
                    value={action.id}
                    disabled={action.disabled}
                    className="text-white hover:bg-white/10 focus:bg-white/10 focus:text-white data-[disabled]:opacity-50"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      <span>
                        {action.label}
                        {action.hint && (
                          <span className="block text-[10px] text-gray-400">{action.hint}</span>
                        )}
                      </span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* No targets left for this action - explain instead of an empty list */}
          {(noTargetsLeft || consultBlocked) && (
            <div
              data-testid="action-no-targets-hint"
              className="min-h-touch flex-[1.5] flex items-center rounded-md border border-white/10 bg-black/30 px-2 text-[11px] text-gray-400"
            >
              {emptyTargetHint}
            </div>
          )}

          {/* Target Selection - only shows when action needs target */}
          {needsTarget && !noTargetsLeft && (
            <Select value={selectedTarget} onValueChange={(value) => {
              setSelectedTarget(value);
              setActionError(null);
              setLastFailedAction(null);
            }}>
              <SelectTrigger className="min-h-touch flex-[1.5] bg-black/40 border-2 border-white/30 text-white shadow-lg">
                <div className="flex items-center gap-2 w-full overflow-hidden">
                  <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: themeAccent }}></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: themeAccent }}></span>
                  </span>
                  <span className="truncate text-xs">
                    <SelectValue placeholder="Select target..." />
                  </span>
                </div>
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/20">
                {getTargets().map((target) => (
                  <SelectItem key={target.value} value={target.value} className="text-white hover:bg-white/10 focus:bg-white/10 focus:text-white">
                    {target.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Execute Button */}
          <Button
            onClick={() => handleAction()}
            disabled={processing || noTargetsLeft || consultBlocked || isGameOver}
            className="min-h-touch px-4 font-semibold"
            style={{ backgroundColor: themeAccent }}
          >
            {processing ? '...' : 'Go'}
          </Button>
        </div>
      </div>

      {/* Desktop Layout - single row (hidden below md) */}
      <div className="hidden md:flex items-center gap-2">
        {/* Menu - always on left */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="bg-black/40 border-white/20 text-white hover:bg-white/10"
            >
              <Menu className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          {menuContent}
        </DropdownMenu>

        {/* Spacer - pushes everything else to the right */}
        <div className="flex-1" />

        {/* Quick Action Buttons */}
        <Button
          onClick={() => setShowCaseFile(true)}
          variant="outline"
          className="bg-black/40 border-white/20 text-white hover:bg-white/10"
          style={{ borderColor: `${themeAccent}40` }}
        >
          <FolderOpen className="w-4 h-4 mr-1" />
          Case File
        </Button>

        <Button
          onClick={() => setShowEvidenceBoard(true)}
          variant="outline"
          className="bg-black/40 border-white/20 text-white hover:bg-white/10"
          style={{ borderColor: `${themeAccent}40` }}
        >
          <Grid3X3 className="w-4 h-4 mr-1" />
          Evidence
        </Button>

        <Button
          onClick={() => setShowSuspectWall(true)}
          variant="outline"
          className="bg-black/40 border-white/20 text-white hover:bg-white/10"
          style={{ borderColor: `${themeAccent}40` }}
        >
          <Users className="w-4 h-4 mr-1" />
          Suspects
        </Button>

        {/* Cipher Button - only shows when unlocked */}
        {cipherState?.unlocked && (
          <Button
            onClick={() => setShowCipherModal(true)}
            disabled={processing}
            variant="outline"
            className={`${
              cipherState?.solved
                ? 'bg-green-900/40 border-green-500/40 text-green-400 hover:bg-green-900/60'
                : 'bg-black/40 border-white/20 text-white hover:bg-white/10'
            }`}
            style={{ borderColor: cipherState?.solved ? undefined : `${themeAccent}40` }}
          >
            {cipherState?.solved ? (
              <>
                <Check className="w-4 h-4 mr-1" />
                Decoded
              </>
            ) : (
              <>
                <KeyRound className="w-4 h-4 mr-1" style={{ color: themeAccent }} />
                Cipher
              </>
            )}
          </Button>
        )}

        {/* Divider */}
        <div className="h-8 w-px bg-white/20 mx-1" />

        {/* Action Selection */}
        <Select value={selectedAction} onValueChange={(value) => {
          setSelectedAction(value);
          setActionError(null);
          setLastFailedAction(null);
        }}>
          <SelectTrigger className="w-48 bg-black/40 border-white/20 text-white">
            <SelectValue placeholder="Choose action..." />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-white/20">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <SelectItem
                  key={action.id}
                  value={action.id}
                  disabled={action.disabled}
                  className="text-white hover:bg-white/10 focus:bg-white/10 focus:text-white data-[disabled]:opacity-50"
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <span>
                      {action.label}
                      {action.hint && (
                        <span className="block text-[10px] text-gray-400">{action.hint}</span>
                      )}
                    </span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* No targets left for this action - explain instead of an empty list */}
        {(noTargetsLeft || consultBlocked) && (
          <div
            data-testid="action-no-targets-hint-desktop"
            className="w-56 flex items-center rounded-md border border-white/10 bg-black/30 px-3 py-2 text-xs text-gray-400"
          >
            {emptyTargetHint}
          </div>
        )}

        {/* Target Selection - only shows when action needs target */}
        {needsTarget && !noTargetsLeft && (
          <Select value={selectedTarget} onValueChange={(value) => {
            setSelectedTarget(value);
            setActionError(null);
            setLastFailedAction(null);
          }}>
            <SelectTrigger className="w-56 bg-black/40 border-2 border-white/30 text-white shadow-lg">
              <div className="flex items-center gap-2 w-full overflow-hidden">
                <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: themeAccent }}></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: themeAccent }}></span>
                </span>
                <span className="truncate">
                  <SelectValue placeholder="Select target..." />
                </span>
              </div>
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/20">
              {getTargets().map((target) => (
                <SelectItem key={target.value} value={target.value} className="text-white hover:bg-white/10 focus:bg-white/10 focus:text-white">
                  {target.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Execute Button */}
        <Button
          onClick={() => handleAction()}
          disabled={processing || noTargetsLeft || consultBlocked || isGameOver}
          className="px-6 font-semibold"
          style={{ backgroundColor: themeAccent }}
        >
          {processing ? '...' : 'Go'}
        </Button>

        {/* Accuse Button */}
        <Button
          onClick={onAccuse}
          disabled={isGameOver}
          data-testid="desktop-accuse"
          variant="destructive"
          className="px-4 font-semibold bg-red-600 hover:bg-red-700"
        >
          <AlertTriangle className="w-4 h-4 mr-1.5" />
          Accuse
        </Button>
      </div>

      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-white/20">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">
              {isFeaturedSessionId(sessionId) ? 'Restart Case?' : 'Reset Case?'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {isFeaturedSessionId(sessionId)
                ? 'This will clear all progress and start this featured case again from the beginning.'
                : 'This will clear all progress and return you to theme selection.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
              <p className="text-red-200 text-sm">
                <strong>Warning:</strong> All investigation progress will be lost.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowResetConfirm(false)}
                disabled={processing}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReset}
                disabled={processing}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                {isFeaturedSessionId(sessionId) ? 'Restart Case' : 'Reset Case'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Case Confirmation Dialog */}
      <Dialog open={showNewCaseConfirm} onOpenChange={setShowNewCaseConfirm}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-white/20">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">Start New Case?</DialogTitle>
            <DialogDescription className="text-gray-400">
              Your current case will be saved to the archive.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <p className="text-blue-200 text-sm">
                You can return to this case later from the Case Archive.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowNewCaseConfirm(false)}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleNewCase}
                className="flex-1 font-semibold"
                style={{ backgroundColor: themeAccent }}
              >
                Start New Case
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Case File Dialog */}
      <CaseFile
        open={showCaseFile}
        onClose={() => setShowCaseFile(false)}
      />

      {/* Case Summary Dialog — the same modal the accusation result opens, so
          the fetch lives in one place. Mounted only once the case is over:
          `CaseSummaryModal` fetches on `open`, and the route 400s mid-game. */}
      {isGameOver && (
        <CaseSummaryModal
          open={showCaseSummary}
          onClose={() => setShowCaseSummary(false)}
          sessionId={sessionId}
        />
      )}

      {/* Evidence Board Dialog */}
      <EvidenceBoard
        open={showEvidenceBoard}
        onClose={() => setShowEvidenceBoard(false)}
      />

      {/* Suspect Wall Dialog */}
      <SuspectWall
        open={showSuspectWall}
        onClose={() => setShowSuspectWall(false)}
        onStartInterrogation={(suspect, roundsCompleted) => {
          setInterrogationSuspect(suspect);
          setInterrogationRoundsCompleted(roundsCompleted);
          setSelectedCharacter({ ...suspect, type: 'suspect' });
          // Preserve narrative for cancel-restore; never write an empty
          // narrativeText to the store (it trips the session-authority guard)
          setPreviousNarrative(gameState.narrativeText);
          setShowInterrogation(true);
        }}
      />

      {/* Timeline Dialog */}
      <Timeline
        open={showTimeline}
        onClose={() => setShowTimeline(false)}
      />

      {/* Relationship Map Dialog */}
      <RelationshipMap
        open={showRelationshipMap}
        onClose={() => setShowRelationshipMap(false)}
      />

      {/* News Modal */}
      <NewsModal
        open={showNews}
        onClose={() => {
          setShowNews(false);
          setCurrentNews(null);
          setNewsLoading(false);
        }}
        article={currentNews}
        loading={newsLoading}
      />

      {/* Interrogation Modal */}
      <InterrogationModal
        open={showInterrogation}
        onClose={() => {
          setShowInterrogation(false);
          setInterrogationSuspect(null);
          // Restore previous narrative if interrogation was cancelled
          if (previousNarrative) {
            updateGameState({ narrativeText: previousNarrative });
            setPreviousNarrative('');
          }
        }}
        suspect={interrogationSuspect}
        onComplete={handleInterrogationComplete}
        initialRoundsCompleted={interrogationRoundsCompleted}
        onRefetchSession={refetchSession}
      />

      {/* Examine Modal - Focus Selection & Results */}
      <ExamineModal
        open={showExamineModal}
        onClose={() => {
          setShowExamineModal(false);
          setExamineLocation(null);
        }}
        location={examineLocation}
        onComplete={handleExamineComplete}
        usedFocuses={gameState.examinedFocuses?.[examineLocation?.id] || []}
        previousExaminations={gameState.examinations?.[examineLocation?.id]?.length || 0}
      />

      {/* Cipher Modal */}
      <CipherModal
        open={gameState.showCipherModal}
        onClose={() => setShowCipherModal(false)}
      />

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal
        open={showKeyboardShortcuts}
        onClose={() => setShowKeyboardShortcuts(false)}
      />

      {/* Discovery Notifications. Portalled to <body> like every Radix overlay
          here: the toasts position against the viewport, and on /game3d this
          bar rides a transformed HUD wrapper that would otherwise drag them
          off-screen with it. Radix dialogs mount their own body portals later,
          so they still paint above these at the same z-50. */}
      {createPortal(<DiscoveryNotification />, document.body)}

      {/* Settings Menu */}
      <SettingsMenu
        open={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </div>
  );
};
